import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import eiReceiptService from '../../services/EIReceiptService.js';
import tankService from '../../services/TankService.js';
import userService from '../../services/UserService.js';
import locationService from '../../services/LocationService.js';
import { ClipboardCheck, ArrowLeft, Calendar, ChevronDown } from "lucide-react";
import { useAuth } from '../../context/AuthContext.js';
import usePermissions from '../../hooks/usePermissions';
import { shouldLockInspectorInEdit } from '../../utils/roleBasedUIHelpers.js';
import {
  DEFAULT_INTERNAL_REMARKS,
  normalizeInternalRemarks,
  finalizeInternalRemarksForSubmit,
} from '../../utils/eiReceiptDefaults.js';

const EditEIReceipt = () => {
  const [generalConditionOpen, setGeneralConditionOpen] = useState(false);
  const { receiptId } = useParams();
  const [formData, setFormData] = useState({
    client_id: '',
    tank_id: '',
    date_of_survey: '',
    location_id: '',
    last_cargo_carried: '',
    cha: '',
    manlid_seal_no: '',
    airline_v_seal_no: '',
    bottom_seal_no: '',
    top_discharge_seal_no: '',
    published: false,
    send_to_dm: false,
    inspected_by_id: '',
    
    tank_no: '',
    tank_type: '',
    maximum_gross_weight: '',
    tare_weight: '',
    capacity_ltr: '',
    csc: '',
    depot_name: '',
    manufacture_date: '',
    ned: '',
    
    general_condition: {
      frame_tank_walkways_free: 'NO',
      manlid_valve_compartments_free: 'NO',
      serial_numbers_legible: 'NO',
      unit_having_steam_jacket: 'NO',
      unit_having_bottom_seal: 'NO',
      unit_having_gasket_on_manlid: 'NO',
      dipstick_originally_fitted: 'NO',
      calibration_chart_fitted: 'NO',
      siphon_tube_originally_fitted: 'NO',
      no_cuts_on_ladder_and_walkway: 'NO',
      pressure_gauge_fitted: 'NO',
      temperature_gauge_fitted: 'NO',
      prv_with_popset: 'NO',
      top_discharge_provision: 'NO',
      rfid_gps_unit: 'NO',
      interior_clean_dry_and_odourless: 'NO',
      valves_fittings_free_from_previous_cargo: 'NO',
      walk_way_type: ''
    },
    
    internal_remarks: { ...DEFAULT_INTERNAL_REMARKS },
    remarks: [],
  });

  const [clients, setClients] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newInternalRemark, setNewInternalRemark] = useState('');
  const [newAdditionalInternalRemark, setNewAdditionalInternalRemark] = useState('');
  const [useExistingTank, setUseExistingTank] = useState(false);
  const { user } = useAuth();
  const {
    isSystemAdmin,
    isAdmin,
    isDocumentationManager,
    isSurveyInspector,
    isAccountant,
  } = usePermissions();
  const canEditPublishFields = isSystemAdmin || isAdmin || isDocumentationManager;
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
    fetchInspectors();
    fetchLocations();
    fetchReceipt();
  }, [receiptId]);

  useEffect(() => {
    if (useExistingTank && formData.client_id) {
      fetchClientTanks(formData.client_id);
    } else if (!useExistingTank) {
      setTanks([]);
    }
  }, [formData.client_id, useExistingTank]);

  useEffect(() => {
    if (useExistingTank && formData.tank_id) {
      loadTankData(formData.tank_id);
    }
  }, [formData.tank_id, useExistingTank]);

  const fetchClients = async () => {
    try {
      const response = await userService.getClients(null, null, { status: 'active' });
      setClients(response.data || []);
    } catch (err) {
      Alert.error('Failed to load clients');
      console.error(err);
    }
  };

  const fetchClientTanks = async (clientId) => {
    try {
      const response = await tankService.getByClientId(clientId);
      setTanks(response.data || []);
    } catch (err) {
      Alert.error('Failed to load tanks');
      console.error(err);
    }
  };

  const fetchInspectors = async () => {
    try {
      const response = await userService.getByRole(4);
      setInspectors(response.data || []);
    } catch (err) {
      console.error('Failed to load inspectors:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationService.getAllLocations();
      setLocations(response.data || []);
    } catch (err) {
      Alert.error('Failed to load locations');
      console.error(err);
    }
  };

  const loadTankData = async (tankId) => {
    try {
      const response = await tankService.getById(tankId);
      const tank = response.data;
      
      setFormData(prev => ({
        ...prev,
        tank_no: tank.tank_no || '',
        tank_type: tank.tank_type || '',
        maximum_gross_weight: tank.maximum_gross_weight || '',
        tare_weight: tank.tare_weight || '',
        capacity_ltr: tank.capacity_ltr || '',
        csc: tank.csc || '',
        depot_name: tank.depot_name || '',
        manufacture_date: tank.manufacture_date || '',
        ned: tank.ned || ''
      }));
    } catch (err) {
      Alert.error('Failed to load tank data');
      console.error(err);
    }
  };

  const handleUseExistingTankToggle = (e) => {
    const checked = e.target.checked;
    setUseExistingTank(checked);

    if (checked) {
      if (formData.client_id) {
        fetchClientTanks(formData.client_id);
      }
    } else {
      setTanks([]);
      setFormData(prev => ({
        ...prev,
        tank_id: '',
        tank_no: '',
        tank_type: '',
        maximum_gross_weight: '',
        tare_weight: '',
        capacity_ltr: '',
        csc: '',
        depot_name: '',
        manufacture_date: '',
        ned: ''
      }));
    }
  };

  const fetchReceipt = async () => {
    try {
      const response = await eiReceiptService.getById(receiptId);
      const receipt = response.data;
      
      setFormData({
        client_id: receipt.client_id,
        tank_id: receipt.tank_id,
        date_of_survey: receipt.date_of_survey,
        location_id: receipt.location_id || '',
        last_cargo_carried: receipt.last_cargo_carried || '',
        cha: receipt.cha || '',
        manlid_seal_no: receipt.manlid_seal_no || '',
        airline_v_seal_no: receipt.airline_v_seal_no || '',
        bottom_seal_no: receipt.bottom_seal_no || '',
        top_discharge_seal_no: receipt.top_discharge_seal_no || '',
        published: receipt.published,
        send_to_dm: receipt.send_to_dm || false,
        inspected_by_id: receipt.inspected_by_id,
        
        tank_no: receipt.tank_no || '',
        tank_type: receipt.tank_type || '',
        maximum_gross_weight: receipt.maximum_gross_weight || '',
        tare_weight: receipt.tare_weight || '',
        capacity_ltr: receipt.capacity_ltr || '',
        csc: receipt.csc || '',
        depot_name: receipt.depot_name || '',
        manufacture_date: receipt.manufacture_date || '',
        ned: receipt.ned || '',
        
        general_condition: receipt.general_condition || {
          frame_tank_walkways_free: 'NO',
          manlid_valve_compartments_free: 'NO',
          serial_numbers_legible: 'NO',
          unit_having_steam_jacket: 'NO',
          unit_having_bottom_seal: 'NO',
          unit_having_gasket_on_manlid: 'NO',
          dipstick_originally_fitted: 'NO',
          calibration_chart_fitted: 'NO',
          siphon_tube_originally_fitted: 'NO',
          no_cuts_on_ladder_and_walkway: 'NO',
          pressure_gauge_fitted: 'NO',
          temperature_gauge_fitted: 'NO',
          prv_with_popset: 'NO',
          top_discharge_provision: 'NO',
          rfid_gps_unit: 'NO',
          interior_clean_dry_and_odourless: 'NO',
          valves_fittings_free_from_previous_cargo: 'NO',
          walk_way_type: ''
        },
        
        internal_remarks: normalizeInternalRemarks(receipt.internal_remarks),
        remarks: Array.isArray(receipt.remarks) ? receipt.remarks : [],
      });

      // If receipt has a tank_id, enable the existing tank toggle
      if (receipt.tank_id) {
        setUseExistingTank(true);
      }
    } catch (err) {
      Alert.error('Failed to load EI receipt data');
      console.error(err);
      navigate('/ei-receipts');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleGeneralConditionChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      general_condition: {
        ...prev.general_condition,
        [field]: value
      }
    }));
  };

  const handleInternalRemarksFieldChange = (field, value) => {
    setFormData(prev => ({
        ...prev,
        internal_remarks: {
        ...prev.internal_remarks,
        [field]: value
        }
    }));
    };

    const addAdditionalInternalRemark = () => {
    const text = newAdditionalInternalRemark.trim();
    if (!text) return;
    setFormData((prev) => ({
      ...prev,
      internal_remarks: {
        ...prev.internal_remarks,
        additional_remarks: [
          ...(prev.internal_remarks.additional_remarks || []),
          text,
        ],
      },
    }));
    setNewAdditionalInternalRemark('');
    };

    const removeAdditionalInternalRemark = (index) => {
    setFormData(prev => ({
        ...prev,
        internal_remarks: {
        ...prev.internal_remarks,
        additional_remarks: prev.internal_remarks.additional_remarks.filter((_, i) => i !== index)
        }
    }));
    };

  const handleRemarksKeyDown = (e) => {
    const textarea = e.target;
    const cursorPos = textarea.selectionStart;
    const currentVal = textarea.value;

    if (e.key === 'Enter' && !e.shiftKey) {
      // Enter alone = new bullet point on next line
      e.preventDefault();
      const insertion = '\n• ';
      const newVal = currentVal.slice(0, cursorPos) + insertion + currentVal.slice(cursorPos);
      const newCursorPos = cursorPos + insertion.length;
      textarea.value = newVal;
      textarea.selectionStart = newCursorPos;
      textarea.selectionEnd = newCursorPos;
      // Sync to state
      const lines = newVal.split('\n').map(l => l.startsWith('• ') ? l.slice(2) : l);
      setFormData(prev => ({ ...prev, remarks: lines }));

    } else if (e.key === 'Enter' && e.shiftKey) {
      // Shift+Enter = new line within same bullet (default textarea behavior), just sync
      // Let browser insert the \n, then sync in handleRemarksChange — no prefix added
      // Nothing to intercept here, fall through to default

    } else if (e.key === 'Backspace') {
      // If cursor is right after the bullet prefix "• " at start of a line, skip over the prefix
      const lineStart = currentVal.lastIndexOf('\n', cursorPos - 1) + 1;
      const charsFromLineStart = cursorPos - lineStart;
      if (charsFromLineStart === 2 && currentVal.slice(lineStart, lineStart + 2) === '• ') {
        // Cursor is right after "• " — delete the whole bullet prefix + the \n before it
        e.preventDefault();
        const newVal = currentVal.slice(0, lineStart - 1) + currentVal.slice(lineStart + 2);
        const newCursorPos = lineStart - 1;
        textarea.value = newVal;
        textarea.selectionStart = newCursorPos;
        textarea.selectionEnd = newCursorPos;
        const lines = newVal.split('\n').map(l => l.startsWith('• ') ? l.slice(2) : l);
        setFormData(prev => ({ ...prev, remarks: lines }));
      }
      // Otherwise: default backspace behavior, handleRemarksChange will sync
    }
  };

  const handleRemarksChange = (e) => {
    // Sync raw textarea value to state (strips bullet prefix per line for storage)
    const raw = e.target.value;
    const lines = raw.split('\n').map(l => l.startsWith('• ') ? l.slice(2) : l);
    setFormData(prev => ({ ...prev, remarks: lines }));
  };

  const getRemarksTextareaValue = () => {
    return (formData.remarks || [])
      .filter(r => r !== undefined)
      .map(r => '• ' + r)
      .join('\n');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const cleanedData = { ...formData };
      cleanedData.remarks = (cleanedData.remarks || []).filter((r) => r && String(r).trim() !== '');
      cleanedData.internal_remarks = finalizeInternalRemarksForSubmit(
        cleanedData.internal_remarks,
        newAdditionalInternalRemark
      );
      
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '' && !['general_condition', 'internal_remarks', 'remarks'].includes(key)) {
          cleanedData[key] = null;
        }
      });

      await eiReceiptService.update(receiptId, cleanedData);
      Alert.success('EI receipt record updated successfully!');
      setTimeout(() => navigate('/ei-receipts'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update EI receipt record');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Loading EI receipt data...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-6">
              <div className="max-w-8xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <ClipboardCheck className="w-5 h-5 text-buttonBg" /> Edit EI Receipt
                    </h1>
                  </div>
                  <button
                    onClick={() => navigate('/ei-receipts')}
                    className="relative overflow-hidden group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm rounded-lg border border-buttonBg bg-buttonBg text-buttonTxt font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:bg-transparent hover:text-buttonBg">
                    {/* COLLAPSING BACKGROUND */}
                    <span className="absolute inset-0 bg-buttonBg scale-x-100 group-hover:scale-x-0 origin-center transition-transform duration-300 ease-out z-0"/>
                    {/* ICON */}
                    <ArrowLeft className="w-4 h-4 relative z-10 transition-transform duration-300 ease-out group-hover:scale-90"/>
                    <span className="relative z-10">Back to EI Reciept List</span>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
                  {/* Section 1: Basic Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Basic Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Client <span className="text-danger font-bold">*</span>
                        </label>
                        <select
                          name="client_id"
                          value={formData.client_id}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          required
                          disabled
                        >
                          <option value="">Select Client</option>
                          {clients.map((client) => (
                            <option key={client.client_id} value={client.client_id}>
                              {client.name} - {client.organization_name}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Client cannot be changed</p>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-1 sm:mb-2">
                          <input
                            type="checkbox"
                            id="use_existing_tank"
                            checked={useExistingTank}
                            onChange={handleUseExistingTankToggle}
                            className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded accent-buttonBg focus:ring-buttonBg"
                            disabled={!formData.client_id}
                          />
                          <label htmlFor="use_existing_tank" className="text-xs sm:text-sm font-medium text-secondary">
                            Use Existing Tank
                          </label>
                        </div>
                        {useExistingTank && (
                          <select
                            name="tank_id"
                            value={formData.tank_id}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                            required={useExistingTank}
                            disabled={!formData.client_id}
                          >
                            <option value="">Select Tank</option>
                            {tanks.map((tank) => (
                              <option key={tank.id} value={tank.id}>
                                {tank.tank_no || `Tank ${tank.id}`}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Date of Survey <span className="text-danger font-bold">*</span>
                        </label>
                        <div className='relative'>
                          <input
                            type="date"
                            name="date_of_survey"
                            value={formData.date_of_survey}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                            required
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Location <span className="text-danger font-bold">*</span>
                        </label>
                        <select
                          name="location_id"
                          value={formData.location_id}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Location</option>
                          {locations.map((location) => (
                            <option key={location.id} value={location.id}>
                              {location.city}, {location.state} ({location.city_code})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Inspector <span className="text-danger font-bold">*</span>
                          {isSurveyInspector && <span className="text-blue-600 ml-2">(Locked)</span>}
                        </label>
                        <select
                          name="inspected_by_id"
                          value={formData.inspected_by_id}
                          onChange={handleChange}
                          disabled={isSurveyInspector}
                          className={`w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base ${isSurveyInspector ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          required
                        >
                          <option value="">Select Inspector</option>
                          {inspectors.map((inspector) => (
                            <option key={inspector.user_id} value={inspector.user_id}>
                              {inspector.name}{isSurveyInspector && inspector.user_id === user?.user_id ? ' (You)' : ''} - {inspector.email}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Last Cargo Carried</label>
                        <input
                          type="text"
                          name="last_cargo_carried"
                          value={formData.last_cargo_carried}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">CHA</label>
                        <input
                          type="text"
                          name="cha"
                          value={formData.cha}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      {(() => {
                        const showPublishCheckbox = canEditPublishFields;
                        const showSendToDMCheckbox = true;
                        const sendToDMDisabled = false;
                        const publishDisabled = !canEditPublishFields;
                        return (
                          <>
                            {showSendToDMCheckbox && (
                              <div className="flex items-center gap-2 mt-8">
                                <input
                                  type="checkbox"
                                  id="send_to_dm"
                                  name="send_to_dm"
                                  checked={formData.send_to_dm}
                                  onChange={handleChange}
                                  className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded accent-buttonBg focus:ring-buttonBg"
                                  disabled={sendToDMDisabled}
                                />
                                <label htmlFor="send_to_dm" className="text-xs sm:text-sm font-medium text-secondary">
                                  Send to DM
                                </label>
                              </div>
                            )}
                            {showPublishCheckbox && (
                              <div className="flex items-center gap-2 mt-2">
                                <input
                                  type="checkbox"
                                  id="published"
                                  name="published"
                                  checked={formData.published}
                                  onChange={handleChange}
                                  className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded accent-buttonBg focus:ring-buttonBg"
                                  disabled={publishDisabled}
                                />
                                <label htmlFor="published" className="text-xs sm:text-sm font-medium text-secondary">
                                  Publish to Client
                                </label>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Section 2: Seal Numbers */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Seal Numbers</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Manlid Seal No</label>
                        <input
                          type="text"
                          name="manlid_seal_no"
                          value={formData.manlid_seal_no}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Airline V/Seal No</label>
                        <input
                          type="text"
                          name="airline_v_seal_no"
                          value={formData.airline_v_seal_no}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Bottom Seal No</label>
                        <input
                          type="text"
                          name="bottom_seal_no"
                          value={formData.bottom_seal_no}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Top Discharge Seal No</label>
                        <input
                          type="text"
                          name="top_discharge_seal_no"
                          value={formData.top_discharge_seal_no}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Tank Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Tank Details (From Selected Tank)
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Tank Container No</label>
                        <input
                          type="text"
                          name="tank_no"
                          value={formData.tank_no}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Type</label>
                        <input
                          type="text"
                          name="tank_type"
                          value={formData.tank_type}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Max Gross Wt.</label>
                        <input
                          type="text"
                          name="maximum_gross_weight"
                          value={formData.maximum_gross_weight}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Tare Wt.</label>
                        <input
                          type="text"
                          name="tare_weight"
                          value={formData.tare_weight}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Capacity</label>
                        <input
                          type="text"
                          name="capacity_ltr"
                          value={formData.capacity_ltr}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">C.S.C</label>
                        <input
                          type="text"
                          name="csc"
                          value={formData.csc}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Depot Name</label>
                        <input
                          type="text"
                          name="depot_name"
                          value={formData.depot_name}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Manufacturing Date</label>
                        <div className='relative'>
                          <input
                            type="date"
                            name="manufacture_date"
                            value={formData.manufacture_date || ''}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Next Test Date</label>
                        <div className='relative'>
                          <input
                            type="date"
                            name="ned"
                            value={formData.ned || ''}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 4: General Condition */}
                  <div className="border border-gray-200 mb-4">

                    {/* Header */}
                    <button
                      type="button"
                      onClick={() => setGeneralConditionOpen(!generalConditionOpen)}
                      className="w-full flex justify-between items-center px-3 py-2 sm:px-4 sm:py-3 bg-gray-50 rounded-t-lg"
                    >
                      <h3 className="text-base sm:text-md font-semibold text-buttonBg">
                        General Condition
                      </h3>

                      <ChevronDown
                        size={20}
                        className={`text-gray-600 transition-transform duration-300 ${
                          generalConditionOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {/* Content */}
                    {generalConditionOpen && (
                      <div className="p-3 sm:p-4 space-y-3">

                        {Object.entries({
                          frame_tank_walkways_free: 'Frame, Tank and walkways free of contamination and cargo',
                          manlid_valve_compartments_free: 'Manlid and valve compartments free of contamination and cargo',
                          serial_numbers_legible: 'Serial numbers and statutory marking legible',
                          unit_having_steam_jacket: 'Unit having steam jacket',
                          unit_having_bottom_seal: 'Unit having bottom seal provision for bullet seal',
                          unit_having_gasket_on_manlid: 'Unit having gasket on manlid cover',
                          dipstick_originally_fitted: 'Dipstick originally fitted',
                          calibration_chart_fitted: 'Calibration chart fitted',
                          siphon_tube_originally_fitted: 'Siphon tube originally fitted',
                          no_cuts_on_ladder_and_walkway: 'No cuts on ladder and walkway',
                          pressure_gauge_fitted: 'Pressure gauge fitted',
                          temperature_gauge_fitted: 'Temperature gauge fitted',
                          prv_with_popset: 'PRV with Poppet',
                          top_discharge_provision: 'Top discharge provision',
                          rfid_gps_unit: 'RFID GPS unit',
                          interior_clean_dry_and_odourless: 'Interior clean dry and odourless',
                          valves_fittings_free_from_previous_cargo: 'VALVES/FITTINGS free from previous cargo'
                        }).map(([key, label]) => (
                          <div key={key} className="flex items-center gap-4">
                            <span className="flex-1 text-xs sm:text-sm text-secondary">
                              {label}
                            </span>

                            <div className="flex gap-4">
                              <label className="flex items-center gap-1">
                                <input
                                  type="radio"
                                  name={`gc_${key}`}
                                  checked={formData.general_condition[key] === 'YES'}
                                  onChange={() => handleGeneralConditionChange(key, 'YES')}
                                  className="w-4 h-4 text-orange-600 focus:ring-orange-500"
                                />
                                <span className="text-xs sm:text-sm text-tertiary font-medium">
                                  YES
                                </span>
                              </label>

                              <label className="flex items-center gap-1">
                                <input
                                  type="radio"
                                  name={`gc_${key}`}
                                  checked={formData.general_condition[key] === 'NO'}
                                  onChange={() => handleGeneralConditionChange(key, 'NO')}
                                  className="w-4 h-4 text-red-600 focus:ring-red-500"
                                />
                                <span className="text-xs sm:text-sm text-tertiary font-medium">
                                  NO
                                </span>
                              </label>

                              <label className="flex items-center gap-1">
                                <input
                                  type="radio"
                                  name={`gc_${key}`}
                                  checked={formData.general_condition[key] === 'NOT SIGHTED'}
                                  onChange={() => handleGeneralConditionChange(key, 'NOT SIGHTED')}
                                  className="w-4 h-4 text-yellow-600 focus:ring-yellow-500"
                                />
                                <span className="text-xs sm:text-sm text-tertiary font-medium">
                                  NOT SIGHTED
                                </span>
                              </label>
                            </div>
                          </div>
                        ))}

                        {/* Walk way type */}
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Walk way type
                          </label>

                          <select
                            value={formData.general_condition.walk_way_type}
                            onChange={(e) =>
                              handleGeneralConditionChange('walk_way_type', e.target.value)
                            }
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                          >
                            <option value="">Select Type</option>
                            <option value="Full">Full</option>
                            <option value="Longitudinal">Longitudinal</option>
                            <option value="Transverse">Transverse</option>
                            <option value="Basic">Basic</option>
                            <option value="H-Type">H-Type</option>
                            <option value="Extended">Extended</option>
                          </select>
                        </div>

                      </div>
                    )}

                  </div>

                {/* Section 5: Internal Remarks */}
                <div>
                <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Internal Remarks</h3>
                <div className="space-y-4">
                    {/* Specific Fields */}
                    <div>
                    <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Non transferable cargo stains on interior shell:
                    </label>
                    <input
                        type="text"
                        value={formData.internal_remarks.non_transferable_cargo_stains || ''}
                        onChange={(e) => handleInternalRemarksFieldChange('non_transferable_cargo_stains', e.target.value)}
                        placeholder="Enter details..."
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                    />
                    </div>

                    <div>
                    <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Polish/Buffing marks in interior:
                    </label>
                    <input
                        type="text"
                        value={formData.internal_remarks.polish_buffing_marks || ''}
                        onChange={(e) => handleInternalRemarksFieldChange('polish_buffing_marks', e.target.value)}
                        placeholder="Enter details..."
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                    />
                    </div>

                    {/* Additional Remarks */}
                    <div>
                    <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">
                        Additional Internal Remarks
                    </label>
                    <div className="flex gap-2">
                        <input
                        type="text"
                        value={newAdditionalInternalRemark}
                        onChange={(e) => setNewAdditionalInternalRemark(e.target.value)}
                        placeholder="Add additional remark..."
                        className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addAdditionalInternalRemark();
                          }
                        }}
                        />
                        <button
                        type="button"
                        onClick={addAdditionalInternalRemark}
                        className="px-4 py-2 bg-orange-500 text-white rounded-sm hover:bg-orange-600 transition-colors text-sm font-medium"
                        >
                        Add
                        </button>
                    </div>
                    
                    {formData.internal_remarks.additional_remarks && formData.internal_remarks.additional_remarks.length > 0 && (
                        <ul className="space-y-2 mt-3">
                        {formData.internal_remarks.additional_remarks.map((remark, index) => (
                            <li key={index} className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                            <span className="flex-1 text-sm">• {remark}</span>
                            <button
                                type="button"
                                onClick={() => removeAdditionalInternalRemark(index)}
                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                                Remove
                            </button>
                            </li>
                        ))}
                        </ul>
                    )}
                    </div>
                </div>
                </div>

                {/* Section 6: Remarks */}
                <div>
                <h3 className="text-base sm:text-md font-semibold text-buttonBg mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Remarks</h3>
                <div>
                    <p className="text-xs text-gray-500 mb-2">Press <strong>Enter</strong> to add a new bullet point. Press <strong>Shift+Enter</strong> to go to the next line within the same bullet.</p>
                    <textarea
                      value={getRemarksTextareaValue()}
                      onChange={handleRemarksChange}
                      onKeyDown={handleRemarksKeyDown}
                      placeholder="Type a remark and press Enter to add a new bullet..."
                      rows={6}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base resize-y"
                    />
                </div>
                </div>

                  {/* Submit Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="
                            relative overflow-hidden
                            group flex items-center justify-center gap-2
                            px-3 sm:px-3 py-2.5 sm:py-2
                            text-xs sm:text-sm
                            rounded-lg
                            border border-buttonBg
                            bg-buttonBg text-buttonTxt
                            font-medium whitespace-nowrap

                            transition-colors duration-300 ease-out
                            hover:bg-transparent hover:text-buttonBg
                          "
                        >
                          {/* COLLAPSING BACKGROUND */}
                          <span
                            className="
                              absolute inset-0
                              bg-buttonBg
                              scale-x-100
                              group-hover:scale-x-0
                              origin-center
                              transition-transform duration-300 ease-out
                              z-0
                            "
                          />

                          {/* ICON 
                          <Eye
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />*/}

                          <span className="relative z-10">{submitting ? 'Updating...' : 'Update EI Receipt'}</span>
                      
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/ei-receipts')}
                      className="
                        relative overflow-hidden
                        group flex items-center justify-center gap-2
                        px-3 sm:px-5 py-2.5 sm:py-2
                        text-xs sm:text-sm
                        rounded-lg
                        border border-buttonBg
                        bg-white text-buttonBg
                        font-medium whitespace-nowrap

                        transition-colors duration-300 ease-out
                        hover:text-white
                      "
                    >
                      {/* ORANGE FILL (COLLAPSE IN ON HOVER) */}
                      <span
                        className="
                          absolute inset-0
                          bg-buttonBg
                          scale-x-0
                          group-hover:scale-x-100
                          origin-center
                          transition-transform duration-300 ease-out
                          z-0
                        "
                      />
					  {/* ICON 
                          <Pencil
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />*/}
                      <span className="relative z-10">Cancel</span>
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditEIReceipt;
